import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createRouter } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { ErrorComponent, NotFoundComponent } from '../.tanstack/routes/__root'
import { routeTree } from '../.tanstack/routeTree.gen'
import type { AuthContext, RouterContext } from './router-context'
import { resolveProfileName } from './configs/profiles'
import { isWxMiniProgram, redirectToMiniProgram } from './utils/wx'

const defaultAuth: AuthContext = {
  isAuthenticated: false,
  permissions: [],
}

interface ApiStatusResponse {
  status?: number
}

const hasStatus = (value: unknown): value is ApiStatusResponse => {
  return typeof value === 'object' && value !== null && 'status' in value
}

const redirectUnauthorized = (data: unknown) => {
  if (!hasStatus(data) || data.status !== 403) return
  if (typeof window === 'undefined') return
  if (window.location.pathname === '/login') return
  if (isWxMiniProgram()) {
    redirectToMiniProgram('/pages/login/login')
    return
  }
  window.location.assign('/login')
}

export function getRouter(auth: AuthContext = defaultAuth) {
  const queryClient = new QueryClient({
    queryCache: new QueryCache({
      onSuccess: redirectUnauthorized,
    }),
    mutationCache: new MutationCache({
      onSuccess: redirectUnauthorized,
    }),
    defaultOptions: {
      queries: {
        staleTime: 60_000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    },
  })

  return createRouter({
    routeTree,
    context: {
      queryClient,
      auth,
      profile: resolveProfileName(import.meta.env.MODE),
    } satisfies RouterContext,
    defaultPreload: false,
    defaultPreloadStaleTime: 0,
    defaultPendingMinMs: 0,
    defaultErrorComponent: ErrorComponent,
    defaultNotFoundComponent: NotFoundComponent,
    scrollRestoration: false,
    Wrap: ({ children }: { children: ReactNode }) => (
      <QueryClientProvider client={queryClient}>
        {children}
      </QueryClientProvider>
    ),
  })
}

type AppRouter = ReturnType<typeof getRouter>

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter
  }
}
